/**
 * CategoryNotFoundError — web-side mirror of the API's port-level error
 * class for `ContentReaderPort.listArticlesByCategory` when the supplied
 * `categorySlug` does not match any existing category.
 *
 * Mirrors `apps/api/src/modules/content/ports/CategoryNotFoundError.ts`
 * by convention, not by import (cross-workspace runtime imports are
 * forbidden — see `./ContentReaderPort.ts` header). Same shape, same
 * semantics: empty-page (`items: []`, `total: 0`) is reserved for
 * "category exists but has no articles"; this class is reserved for
 * "category does not exist".
 *
 * Adapter responsibilities:
 *   - `TrpcContentReader` (TA15) translates the tRPC `NOT_FOUND`
 *     envelope code that the API router emits into an instance of
 *     this class, so consumers see one uniform vocabulary regardless
 *     of adapter.
 *   - Test stubs may throw this directly to simulate the not-found
 *     surface without standing up a real tRPC mock.
 *
 * `Object.setPrototypeOf` restores the prototype chain after `super()`
 * so `err instanceof CategoryNotFoundError` survives subclass
 * transpile-target downshifts. The project targets ES2023; this is
 * defence-in-depth, mirroring the API-side and the web-side
 * `NotImplementedError` (T016).
 */

export class CategoryNotFoundError extends Error {
  readonly categorySlug: string;

  constructor(categorySlug: string) {
    super(`No content category matches slug "${categorySlug}"`);
    this.name = 'CategoryNotFoundError';
    this.categorySlug = categorySlug;
    Object.setPrototypeOf(this, CategoryNotFoundError.prototype);
  }
}
